import { Injectable, Pipe, PipeTransform, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { merge, Subscription } from 'rxjs';

import { equals } from '@awp/core/utils';

import { ContentProvider } from '../content.provider';
import { ContentParser } from '../content.parser';

@Injectable()
@Pipe({
  name: 'content',
  pure: false // required to update the value when the content request is resolved
})
export class ContentPipe implements PipeTransform, OnDestroy {
  value = '';
  lastKey: string;
  lastParams: any[];
  onContentChangedSubscription: Subscription;

  constructor(
    protected contentProvider: ContentProvider,
    contentParser: ContentParser,
    protected changeDetectorRef: ChangeDetectorRef) {

    // any changes in ContentParser.context could result in content being changed
    this.onContentChangedSubscription = merge(contentParser.contextChanged, contentProvider.onLangChanged)
      .subscribe(() => {
        if (this.lastKey) {
          this.updateValue(this.lastKey, this.lastParams);
        }
      });
  }

  updateValue(key: string, interpolateParams?: Object): void {

    // This subscription completes.
    this.contentProvider.get(key, interpolateParams)
      .subscribe((value: string) => {
        this.value = value;
        this.lastKey = key;
        this.changeDetectorRef.markForCheck();
      });
  }

  transform(query: string, ...args: any[]): any {
    if (!query || query.length === 0) {
      return query;
    }

    // if we ask another time for the same key, return the last value
    if (equals(query, this.lastKey) && equals(args, this.lastParams)) {
      return this.value;
    }

    // store the query, in case it changes
    this.lastKey = query;

    // store the params, in case they change
    this.lastParams = args;

    // set the value
    this.updateValue(query, this.interpolateParams(args));

    return this.value;
  }

  ngOnDestroy(): void {
    this.onContentChangedSubscription.unsubscribe();
    this.onContentChangedSubscription = undefined;
  }

  interpolateParams(args: any[]): any {
    let interpolateParams: Object;

    if (args[0] != null && args.length) {
      if (typeof args[0] === 'string' && args[0].length) {
        // we accept objects written in the template such as {n:1}, {'n':1}, {n:'v'}
        // which is why we might need to change it to real JSON objects such as {"n":1} or {"n":"v"}
        const validArgs: string = args[0]
          .replace(/(\')?([a-zA-Z0-9_]+)(\')?(\s)?:/g, '"$2":')
          .replace(/:(\s)?(\')(.*?)(\')/g, ':"$3"');
        try {
          interpolateParams = JSON.parse(validArgs);
        } catch (e) {
          throw new SyntaxError(`Wrong parameter in TranslatePipe. Expected a valid Object, received: ${args[0]}`);
        }
      } else if (typeof args[0] === 'object' && !Array.isArray(args[0])) {
        interpolateParams = args[0];
      }
    }

    return interpolateParams;
  }
}
